import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Lawn Mowing & Care",
  "Hedge Trimming",
  "Landscaping & Design",
  "Planting & Borders",
  "Garden Maintenance",
  "Garden Clearance",
  "Other",
];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! We'll be in touch within one working day.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send your message. Please try again or call us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 px-6 sm:px-10 lg:px-14 bg-brand-moss-deep text-white overflow-hidden"
    >
      {/* Decorative blob */}
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-brand-leaf/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-20 w-[420px] h-[420px] rounded-full bg-brand-terracotta/15 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <span className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-terracotta">
            Let's chat
          </span>
          <h2
            data-testid="contact-heading"
            className="mt-3 font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          >
            Tell us about<br />your garden.
          </h2>
          <p className="mt-6 text-brand-sand/85 text-lg leading-relaxed max-w-md">
            Free, no-obligation quotes — usually back to you the same day. Prefer a
            chat? Give us a ring, we'd rather talk it through.
          </p>

          <div className="mt-10 space-y-4">
            <a
              href="tel:07467946894"
              data-testid="contact-phone"
              className="flex items-center gap-4 group p-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
            >
              <span className="grid place-items-center w-12 h-12 rounded-full bg-brand-terracotta">
                <Phone className="w-5 h-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider text-brand-sand/70">Call us</div>
                <div className="font-display font-semibold text-lg">07467 946894</div>
              </div>
            </a>
            <a
              href="mailto:rafagr2023@gmail.com"
              data-testid="contact-email"
              className="flex items-center gap-4 group p-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
            >
              <span className="grid place-items-center w-12 h-12 rounded-full bg-brand-leaf">
                <Mail className="w-5 h-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider text-brand-sand/70">Email</div>
                <div className="font-display font-semibold text-lg break-all">rafagr2023@gmail.com</div>
              </div>
            </a>
            <a
              href="https://api.whatsapp.com/message/NWVD2NCMR723P1?autoload=1&app_absent=0"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp"
              className="flex items-center gap-4 group p-4 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
            >
              <span className="grid place-items-center w-12 h-12 rounded-full bg-[#25D366]">
                <svg viewBox="0 0 32 32" className="w-5 h-5" fill="currentColor" aria-hidden="true">
                  <path d="M16 0a16 16 0 0 0-13.84 23.96L0 32l8.27-2.16A16 16 0 1 0 16 0Zm0 29.34c-2.34 0-4.6-.62-6.59-1.79l-.47-.28-4.9 1.28 1.31-4.78-.31-.49a13.34 13.34 0 1 1 10.96 6.06Z"/>
                  <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39-.082 0-.166-.024-.245-.06-.93-.422-1.776-.94-2.481-1.66-.198-.205-.554-.527-.554-.838 0-.296.396-.624.633-.832.198-.176.392-.391.392-.66 0-.293-1.105-2.78-1.337-2.972-.144-.116-.354-.116-.527-.116-.146 0-.305-.012-.451 0-.255.024-.532.05-.755.236-.282.232-.927.985-.927 2.21 0 1.293.97 2.55 1.078 2.682 1.21 1.61 2.404 2.625 4.214 3.297.503.18 2.39.74 2.83.474.402-.243.96-.853 1.117-1.378.07-.232.07-.428.05-.467-.03-.082-.122-.11-.275-.196-.166-.083-.876-.434-1.013-.484-.137-.05-.243-.073-.349.075-.106.148-.398.467-.485.563-.087.094-.174.106-.32.038l-.075-.038z"/>
                </svg>
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider text-brand-sand/70">WhatsApp</div>
                <div className="font-display font-semibold text-lg">Quick chat on WhatsApp</div>
              </div>
            </a>
            <div
              data-testid="contact-area"
              className="flex items-center gap-4 p-4 rounded-2xl bg-white/5 border border-white/10"
            >
              <span className="grid place-items-center w-12 h-12 rounded-full bg-white/10">
                <MapPin className="w-5 h-5" />
              </span>
              <div>
                <div className="text-xs uppercase tracking-wider text-brand-sand/70">Service area</div>
                <div className="font-display font-semibold text-lg">Buckinghamshire & Hertfordshire</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-white text-brand-moss-deep rounded-[2rem] p-7 sm:p-10 shadow-2xl"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-brand-moss-deep">
                  Your name *
                </Label>
                <Input
                  id="name"
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Jane Smith"
                  className="mt-2 h-12 rounded-xl border-brand-line bg-brand-sand/40 px-4 focus-visible:ring-brand-moss"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-brand-moss-deep">
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@example.com"
                  className="mt-2 h-12 rounded-xl border-brand-line bg-brand-sand/40 px-4 focus-visible:ring-brand-moss"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-sm font-medium text-brand-moss-deep">
                  Phone
                </Label>
                <Input
                  id="phone"
                  data-testid="contact-input-phone"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="07xxx xxxxxx"
                  className="mt-2 h-12 rounded-xl border-brand-line bg-brand-sand/40 px-4 focus-visible:ring-brand-moss"
                />
              </div>
              <div>
                <Label htmlFor="service" className="text-sm font-medium text-brand-moss-deep">
                  Service of interest
                </Label>
                <select
                  id="service"
                  data-testid="contact-input-service"
                  value={form.service}
                  onChange={update("service")}
                  className="mt-2 h-12 w-full rounded-xl border border-brand-line bg-brand-sand/40 px-4 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-moss"
                >
                  <option value="">Pick one (optional)</option>
                  {SERVICE_OPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-5">
              <Label htmlFor="message" className="text-sm font-medium text-brand-moss-deep">
                Tell us about your garden *
              </Label>
              <Textarea
                id="message"
                data-testid="contact-input-message"
                value={form.message}
                onChange={update("message")}
                placeholder="Garden size, what you'd like done, anything else we should know..."
                rows={5}
                className="mt-2 rounded-xl border-brand-line bg-brand-sand/40 px-4 py-3 focus-visible:ring-brand-moss min-h-[140px]"
                required
              />
            </div>

            <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <p className="text-xs text-brand-charcoal/70">
                We respect your privacy — your details stay with us.
              </p>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="rounded-full bg-brand-moss hover:bg-brand-moss-dark text-white px-8 h-12 font-semibold shadow-md hover:-translate-y-0.5 transition-all w-full sm:w-auto"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Send message <Send className="w-4 h-4" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
